import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Star, Image, Video, FileText, Newspaper, Mail, Users } from 'lucide-react';
import { getBooks, getReviews, getGallery, getVideos, getBlog, getMedia, getContacts } from '../../utils/api';

export default function AdminDashboard() {
  const [counts, setCounts] = useState({ books: 0, reviews: 0, gallery: 0, videos: 0, blog: 0, media: 0, messages: 0 });

  useEffect(() => {
    Promise.all([
      getBooks().catch(() => []),
      getReviews().catch(() => []),
      getGallery().catch(() => []),
      getVideos().catch(() => []),
      getBlog().catch(() => []),
      getMedia().catch(() => []),
      getContacts().catch(() => []),
    ]).then(([books, reviews, gallery, videos, blog, media, messages]) => {
      setCounts({
        books: books.length, reviews: reviews.length, gallery: gallery.length,
        videos: videos.length, blog: blog.length, media: media.length, messages: messages.length,
      });
    });
  }, []);

  const cards = [
    { label: 'Books', count: counts.books, icon: BookOpen, to: '/admin/books', color: 'from-orange-500 to-orange-600' },
    { label: 'Reviews', count: counts.reviews, icon: Star, to: '/admin/reviews', color: 'from-yellow-500 to-orange-500' },
    { label: 'Gallery Photos', count: counts.gallery, icon: Image, to: '/admin/gallery', color: 'from-blue-500 to-blue-600' },
    { label: 'Videos', count: counts.videos, icon: Video, to: '/admin/videos', color: 'from-red-500 to-red-600' },
    { label: 'Blog Posts', count: counts.blog, icon: FileText, to: '/admin/blog', color: 'from-green-500 to-green-600' },
    { label: 'Media Coverage', count: counts.media, icon: Newspaper, to: '/admin/media', color: 'from-purple-500 to-indigo-600' },
    { label: 'Messages', count: counts.messages, icon: Mail, to: '/admin/messages', color: 'from-teal-500 to-cyan-600' },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-1">Dashboard</h1>
      <p className="text-slate-500 text-sm mb-6">Welcome back! Manage all your website content from here.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-all hover:-translate-y-1" data-testid={`dashboard-card-${c.label.toLowerCase().replace(/ /g, '-')}`}>
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.color} flex items-center justify-center mb-4`}>
              <c.icon className="h-6 w-6 text-white" />
            </div>
            <div className="text-3xl font-bold text-slate-900">{c.count}</div>
            <div className="text-sm text-slate-500 mt-1">{c.label}</div>
          </Link>
        ))}
      </div>

      <div className="mt-8 bg-gradient-to-br from-blue-950 to-blue-900 rounded-2xl p-8 text-white">
        <h2 className="text-xl font-bold mb-2">Quick Tips</h2>
        <ul className="space-y-2 text-blue-100 text-sm list-disc list-inside">
          <li>Use the <strong>Author Profile</strong> section to update your bio, mission, and social links.</li>
          <li>Upload photos directly or paste image URLs in any content form.</li>
          <li>For videos, use the YouTube embed format: <code className="bg-white/10 px-2 py-0.5 rounded">https://www.youtube.com/embed/VIDEO_ID</code></li>
          <li>All changes appear instantly on your live website.</li>
        </ul>
      </div>
    </div>
  );
}
